/**
 * UTM Tracking Hook
 * 
 * React hook that automatically extracts, stores, and tracks UTM parameters
 * from URLs. Handles both anonymous and authenticated user tracking.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import {
  extractUTMParams,
  getStoredUTMParams,
  storeUTMParams,
  clearExpiredUTMParams,
  generateSessionId,
  saveUTMToDatabase,
  linkUTMToUser,
  getCurrentUTMParams
} from '@/utils/utmTracking'

/**
 * Hook to automatically track UTM parameters
 * Should be called at the app root level
 */
export const useUTMTracking = () => {
  const location = useLocation()
  const { user } = useAuth()
  const hasInitialized = useRef(false)
  const sessionIdRef = useRef(null)

  useEffect(() => {
    // Generate session ID on mount if not exists
    if (!sessionIdRef.current) {
      sessionIdRef.current = generateSessionId()
    }
  }, [])

  useEffect(() => {
    // Clean up expired params periodically
    clearExpiredUTMParams()
  }, [])

  useEffect(() => {
    // Extract UTM params from current URL
    const urlParams = extractUTMParams(window.location.href)
    
    if (urlParams) {
      // Store in localStorage with 90-day expiry
      storeUTMParams(urlParams)
      
      // Save to database (anonymous if not logged in)
      saveUTMToDatabase(
        urlParams,
        !user,
        sessionIdRef.current,
        user?.id || null
      )
    }
    
    // On initial load, check if we have stored params that should be linked
    if (!hasInitialized.current) {
      hasInitialized.current = true
      
      // If user just logged in, link any stored anonymous UTM data
      if (user?.id) {
        const storedParams = getStoredUTMParams()
        if (storedParams) {
          linkUTMToUser(user.id, sessionIdRef.current)
        }
      }
    }
  }, [location.search, user])

  // Link UTM data when user logs in
  useEffect(() => {
    if (user?.id && hasInitialized.current) {
      const storedParams = getStoredUTMParams()
      if (storedParams) {
        linkUTMToUser(user.id, sessionIdRef.current)
      }
    }
  }, [user?.id])

  // Return current UTM params for use in components
  return getCurrentUTMParams()
}

export default useUTMTracking







